import { createDownloadBlobByFileName } from '@utils/downloadBlob'
import React, { useEffect, useState } from 'react'
import { Button, IconButton, CitySelect, FilterButton, Card, Popover, Icon, LinkButton } from '@ui'
import ImportFlow from 'components/ImportFlow'
import useImport, { Import } from 'components/ImportFlow/hooks/useImport'
import track from '@track'
import { Menu, MenuItem } from '@blueprintjs/core'
import { compact } from 'lodash'
import { ILeases } from './hooks/useLeases'
import Filters from './Filters'
import { isDefault } from './DateFilter'
import { NavigateFn } from '@reach/router'
import { useFileDownload } from '@hooks'
import api from '@api'

interface Props {
  leases: ILeases
  navigate?: NavigateFn
}

export default function Toolbar({ leases, navigate }: Props) {
  let filterCount = 0
  filterCount =
    filterCount +
    compact(Object.entries(leases.filters.date)).filter(
      ([k, v]) => v && v.value && !isDefault(k, v ? v.value : v)
    ).length

  const data = {
    leases,
    navigate,
    filterCount,
  }

  return (
    <Card.Header style={{ justifyContent: 'flex-start', width: '100%' }}>
      <DefaultButtons {...data} />
    </Card.Header>
  )
}

interface DefaultButtonsProps {
  leases: ILeases
  filterCount: number
  navigate?: NavigateFn
}
function DefaultButtons({ leases, filterCount, navigate }: DefaultButtonsProps) {
  const startFileDownload = useFileDownload()
  const [importingLeases, setImportingLeases] = useState(false)
  const importLeases = useImport(Import.LEASE)
  const dlTemplate = createDownloadBlobByFileName('leaseImports_template.csv')

  useEffect(() => {
    if (!leases.exportList) return
    startFileDownload(leases.exportList)
  }, [leases.exportList, startFileDownload])

  return (
    <>
      <CityFilter leases={leases} />
      <div style={{ marginLeft: 10 }} />
      <Popover hasBackdrop content={<Filters {...{ leases }} />}>
        <FilterButton filterCount={filterCount} />
      </Popover>
      {leases.isLoading ? null : filterCount || leases.filters.city || leases.filters.query ? (
        <>
          <span className="mh2">{leases.counters.leases} filtered records</span>{' '}
          <IconButton
            minimal
            className="ml1"
            iconName="times"
            onClick={() => {
              leases.clearFilters()
              track.lease({
                eventName: track.Event.CLEAR_ALL_FILTERS,
              })
            }}
          />
        </>
      ) : (
        <span className="mh2">{leases.counters.leases} records</span>
      )}
      <Button
        className="mla"
        iconName="plus"
        data-button="new_lease"
        onClick={() => {
          navigate?.('?leaseId=new')
          track.lease({
            eventName: track.Event.NEW_RECORD_DIALOG,
          })
        }}
        text="New Lease"
      />
      <Popover
        content={
          <Menu>
            <MenuItem
              text="Download CSV"
              icon={<Icon style={{ marginTop: 2 }} name="download" />}
              onClick={() => {
                leases.fetchExportLink()
                track.lease({
                  eventName: track.Event.EXPORT,
                })
              }}
            />
            <MenuItem
              text="Import Leases"
              icon={<Icon style={{ marginTop: 2 }} name="file-import" />}
              onClick={() => setImportingLeases(true)}
            />
          </Menu>
        }
      >
        <IconButton round={false} className="ml2" tooltip="More actions" iconName="ellipsis-h" />
      </Popover>
      {importingLeases && (
        <ImportFlow
          title="Import Leases"
          importHook={importLeases}
          onClose={() => setImportingLeases(false)}
          excludeUpdated
          onComplete={() => leases.reload()}
        >
          <>
            <p>
              <LinkButton
                onClick={() => api.lease.template().then(dlTemplate)}
                text="Download the .csv template"
              />{' '}
              for bulk operation lease import
            </p>
            <ul>
              <li>You can import multiple leases, or a single lease.</li>
              <li>New data will merge with existing data.</li>
            </ul>
          </>
        </ImportFlow>
      )}
    </>
  )
}

const CityFilter = ({ leases }: { leases: ILeases }) => {
  const onChange = (city: string | null) => {
    if (city === 'All Cities') city = null
    track.lease({
      eventName: track.Event.FILTER,
      props: {
        filterName: 'City',
        filterValue: city,
      },
    })
    leases.filter({ kind: 'city', city })
  }

  const value = leases.filters.city
  const buttonProps = { iconName: 'map-marker-alt' }
  return <CitySelect useAll includeOldBuildings {...{ buttonProps, value, onChange }} />
}
